import { useCurrentFrame, useVideoConfig, spring, interpolate } from 'remotion';
import { ReactNode } from 'react';

// Constants
import { ANIMATION_SPRING_CONFIGS, CLAMP_BOTH } from '@/constants';

// Types
import { AnimationPreset } from '@/types';

export interface WaveTextRevealProps {
  /** The text content to animate */
  text: string;
  /** Optional CSS class names */
  className?: string;
  /** Frame number when animation should start */
  startFrame: number;
  /** Delay between each character animation in frames (default: 1.5) */
  staggerFrames?: number;
  /** Amplitude of the wave motion in pixels (default: 15) */
  waveAmplitude?: number;
}

/**
 * WaveTextReveal - 3D Perspective Wave Text Animation
 *
 * Animates text character by character with a 3D wave effect.
 * Each character flips in from below with a wave-like motion,
 * blur effect, and scale overshoot.
 */
export const WaveTextReveal = ({
  text,
  className = '',
  startFrame,
  staggerFrames = 1.5,
  waveAmplitude = 15,
}: WaveTextRevealProps): ReactNode => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const characters = text.split('');
  const totalChars = characters.length;

  return (
    <span className={`${className} perspective-800`}>
      {characters.map((char, index) => {
        const charDelay = startFrame + index * staggerFrames;

        // Main spring progress
        const rawProgress = spring({
          frame: frame - charDelay,
          fps,
          config: ANIMATION_SPRING_CONFIGS[AnimationPreset.WAVE_TEXT],
        });

        // Snap to 1 when animation is effectively complete to prevent ongoing vibration
        const progress = rawProgress > 0.999 ? 1 : rawProgress;

        // Once fully settled, render as plain inline text to avoid
        // subpixel compositing issues that cause vibration during render.
        const isSettled = progress === 1;

        // Wave offset based on position (only active during animation)
        const wavePhase = (index / totalChars) * Math.PI;
        const waveOffset = isSettled
          ? 0
          : Math.sin(wavePhase + frame * 0.1) * waveAmplitude * (1 - progress);

        // 3D rotation from below
        const rotateX = interpolate(progress, [0, 1], [90, 0], CLAMP_BOTH);

        // Scale with overshoot
        const scale = interpolate(
          progress,
          [0, 0.7, 1],
          [0.3, 1.1, 1],
          CLAMP_BOTH,
        );

        // Opacity
        const opacity = interpolate(progress, [0, 0.3], [0, 1], CLAMP_BOTH);

        // Vertical offset
        const translateY = interpolate(
          progress,
          [0, 1],
          [40 + waveOffset, 0],
          CLAMP_BOTH,
        );

        // Blur effect during animation
        const blur = interpolate(progress, [0, 0.5, 1], [4, 1, 0], CLAMP_BOTH);

        return (
          <span
            key={`${char}-${index}`}
            className={isSettled ? '' : 'inline-block origin-center-bottom'}
            style={{
              ...(!isSettled && {
                opacity,
                transform: `translateY(${translateY}px) rotateX(${rotateX}deg) scale(${scale})`,
                filter: blur > 0 ? `blur(${blur}px)` : 'none',
              }),
            }}
          >
            {char === ' ' ? '\u00A0' : char}
          </span>
        );
      })}
    </span>
  );
};
